import type { Feature } from './Feature.js';

/**
 * Represents all features that can be enabled or disabled for Cloudpack.
 */
export type Features = { [key in keyof typeof allFeatures]?: boolean };

/**
 * All valid feature names.
 */
export type FeatureName = keyof typeof allFeatures;

/**
 * A set of options for configuring the experience.
 * Features can be set to true to change the default behavior.
 */
export const allFeatures = {
  // The comments provide intellisense, and the description strings are used in the help output.

  /** Automatically updates the generated config when an unknown import path is used during start (default: true). */
  autoUpdateEntries: {
    description:
      'Automatically updates the generated config when an unknown import path is used during start (default: true).',
    default: true,
  } as Feature,

  /** Disables the generation of source-maps during build. */
  disableSourceMaps: { description: 'Disables the generation of source-maps during build.' } as Feature,

  /** Enabled cloud-hosted application */
  enableCloudHosted: { description: 'Enables cloud-hosted application.' } as Feature,

  /**
   * Enables support of import maps for workers.
   * To use this feature, the package with the worker must be bundled with webpack (default: true).
   */
  enableModuleWorkers: {
    description:
      'Enables support of import maps for workers. To use this feature, the package with the worker must be bundled with webpack (default: true).',
    default: true,
  } as Feature,

  /** Enables the use of `@parcel/watcher` for file watching (default: true). */
  enableParcelWatcher: {
    description: 'Enables the use of @parcel/watcher for file watching (default: true).',
    default: true,
  } as Feature,

  /**
   * Enables import rewriting for workers instead of using es-module-shims.
   * This makes workers compatible with service workers.
   * Requires enableModuleWorkers to be enabled (default: true).
   */
  rewriteWorkerImports: {
    description:
      'Enables import rewriting for workers instead of using es-module-shims. This makes workers compatible with service workers and removes runtime overhead. Requires enableModuleWorkers to be enabled (default: true).',
    default: true,
  } as Feature,

  /**
   * Enables downloading bundles from remote cache instead of bundling.
   */
  enableRemoteCacheDownloads: {
    description: 'Enables downloading bundles from remote cache instead of bundling.',
  } as Feature,

  /**
   * Enables server-side rendering (SSR) for the application, including Node import map support.
   * This is experimental and may not work in all cases.
   */
  enableSSR: {
    description:
      'Enables server-side rendering (SSR) for the application, including Node import map support. This is experimental and may not work in all cases.',
  } as Feature,

  /** Enables the file watcher to be spawned in a forked process (default: true).  */
  enableWatcherInFork: {
    description: 'Enables the file watcher to be spawned in a forked process (default: true).',
    default: true,
  } as Feature,

  /** Enables shared root file watcher optimization for monorepos. */
  enableWatcherSharedRoot: {
    description: 'Enables shared root file watcher optimization for monorepos.',
  } as Feature,

  /** Enables the preemptive enqueueing of dependencies during build. */
  enqueueDependencies: { description: 'Enables the preemptive enqueueing of dependencies during build.' } as Feature,

  /** Check for dynamic (async) imports with non-literal paths in bundle output during init. */
  evaluateDynamicImports: {
    description: 'Check for dynamic (async) imports with non-literal paths in bundle output during init.',
  } as Feature,

  /** Hot Module Replacement (opt-in, experimental). */
  hmr: {
    description:
      'Experimental! Hot Module Replacement. Allows modules to be updated at runtime without a full refresh.',
  } as Feature,

  /**
   * Excludes unused dependencies in the generated config during an init run.
   * This helps trim the dependency graph, reduce the import map, and speed up performance.
   */
  optimizeDependencies: {
    description:
      // breaking up this string causes an exception in ts-json-schema-generator
      'Excludes unused dependencies in the generated config during an init run. This helps trim the dependency graph, reduce the import map, and speed up performance.',
  } as Feature,

  /** Enables relative resource paths to be used in import map paths when useSingleWebServer is true. (default: true) */
  relativeImportMapPaths: {
    description:
      'Enables relative resource paths to be used in import map paths when useSingleWebServer is true. (default: true)',
    default: true,
  } as Feature,

  /** Creates null entries for unused exports in generated config so that they are not included when bundling. */
  removeUnusedExports: {
    description:
      'Creates null entries for unused exports in generated config so that they are not included when bundling.',
  } as Feature,

  /** @deprecated Removed in favor of `resolve-web-extensions` bundler capability. Enables React Native type resolution of .web.ts extensions. */
  resolveWebExtensions: { description: 'Enables React Native type resolution of .web.ts extensions.' } as Feature,

  /** Enables the syncing of bundles. */
  syncBundles: { description: 'Enables the syncing of bundles.' } as Feature,

  /** Enables the syncing of internal packages. */
  syncInternalPackages: { description: 'Enables the syncing of internal packages.' } as Feature,

  /** Use static analysis instead of bundling during init to determine package imports (experimental). If CJS parsing misses a dependency, the bundle server recovers at runtime and emits a warning. */
  staticAnalysisInit: {
    description:
      'Use static analysis instead of bundling during init to determine package imports (experimental). ' +
      'If CJS parsing misses a dependency, the bundle server recovers at runtime and emits a warning.',
  } as Feature,

  /** Use a single server for both bundles and app assets (default: true). */
  useSingleWebServer: {
    description: 'Use a single server for both bundles and app assets (default: true).',
    default: true,
  } as Feature,

  /** Enables the verification of exports during init (default: true). */
  verifyExports: {
    description: 'Enables the verification of exports during init (default: true).',
    default: true,
  } as Feature,

  // Please add new features in alphabetical order!
  // Don't forget to add them to the doc site as well!
};

/**
 * Record of features that are enabled by default.
 */
export const defaultFeatures = Object.fromEntries(
  Object.entries(allFeatures)
    .filter(([_, feature]) => feature.default)
    .map(([name]) => [name, true]),
) as Features;
